import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API, inr, fdate } from "@/api";
import { Printer, ArrowLeft, Download } from "lucide-react";

export default function QuotationPrint() {
  const { id } = useParams();
  const [q, setQ] = useState(null);
  const [company, setCompany] = useState(null);

  useEffect(() => {
    api.get(`/quotations/${id}`).then((r) => setQ(r.data)).catch(() => {});
    api.get("/settings").then((r) => setCompany(r.data)).catch(() => {});
  }, [id]);

  if (!q || !company)
    return <div className="min-h-screen bg-[#F3F6F5] flex items-center justify-center font-mono2 text-sm text-emerald-600 animate-pulse" data-testid="quotation-print-loading">Loading quotation…</div>;

  const cgst = q.gst_total / 2;

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4" data-testid="portal-quotation-print-preview">
      <div className="max-w-[820px] mx-auto mb-4 flex items-center justify-between no-print">
        <Link to="/app/quotations" className="inline-flex items-center gap-1.5 text-sm text-slate-700 hover:text-emerald-600 transition-colors duration-200" data-testid="quotation-back-link">
          <ArrowLeft className="w-4 h-4" /> Back to quotations
        </Link>
        <div className="flex items-center gap-2">
          <a href={`${API}/quotations/${q.quotation_id}/pdf`} download data-testid="quotation-pdf-download-btn"
            className="inline-flex items-center gap-2 rounded-full border border-slate-500 text-slate-800 hover:border-emerald-400 hover:text-emerald-600 font-semibold text-sm px-5 py-2.5 transition-colors duration-200">
            <Download className="w-4 h-4" /> Download PDF
          </a>
          <button onClick={() => window.print()} data-testid="quotation-print-btn"
            className="inline-flex items-center gap-2 rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-sm px-5 py-2.5 transition-colors duration-200">
            <Printer className="w-4 h-4" /> Print / Save as PDF
          </button>
        </div>
      </div>

      <div className="print-page max-w-[820px] mx-auto bg-white text-slate-900 p-8 shadow-xl" data-testid="quotation-paper">
        <div className="flex items-start justify-between border-b-2 border-emerald-600 pb-5">
          <div className="flex items-start gap-4">
            <img src="/logo.png" alt="GREENRGY" className="h-14" data-testid="quotation-logo" />
            <div>
              <h1 className="text-xl font-bold text-slate-900">{company.company_name}</h1>
              <p className="text-xs text-slate-600 mt-1 max-w-[320px]">{company.address}</p>
              <p className="text-xs text-slate-600">{company.phone} · {company.email}</p>
              <p className="text-xs font-semibold text-slate-800 mt-1">GSTIN: {company.gstin}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-2xl font-extrabold tracking-tight text-emerald-700">PROFORMA INVOICE</p>
            <p className="text-[10px] text-slate-500 italic">Quotation — not a tax document</p>
            <p className="text-sm font-mono font-bold mt-1" data-testid="quotation-number">{q.number}</p>
            <p className="text-xs text-slate-600">Date: {fdate(q.date)}</p>
            <p className="text-xs text-slate-600">Valid Until: {fdate(q.valid_until)}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6 py-5 border-b border-slate-300">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Quoted To</p>
            <p className="font-semibold" data-testid="quotation-buyer-name">{q.client.name}</p>
            {q.client.address && <p className="text-xs text-slate-600 mt-0.5">{q.client.address}</p>}
            {q.client.state && <p className="text-xs text-slate-600">{q.client.state}</p>}
            {q.client.phone && <p className="text-xs text-slate-600">{q.client.phone}</p>}
            {q.client.gstin && <p className="text-xs font-semibold mt-1">GSTIN: {q.client.gstin}</p>}
          </div>
          <div className="text-right">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Price Slab Applied</p>
            <span className="inline-block rounded-full border border-emerald-600 text-emerald-700 text-xs font-semibold px-3 py-1 capitalize" data-testid="quotation-slab-tag">
              {q.slab === "retail" ? "Retail (B2C)" : q.slab}
            </span>
            {q.display_custom?.length > 0 && (
              <div className="mt-3 space-y-1" data-testid="quotation-custom-fields">
                {q.display_custom.map((f, i) => (
                  <p key={i} className="text-xs text-slate-600"><span className="font-semibold text-slate-700">{f.label}:</span> {f.value}</p>
                ))}
              </div>
            )}
          </div>
        </div>

        <table className="w-full text-sm mt-5" data-testid="quotation-items-table">
          <thead>
            <tr className="bg-slate-100 text-left text-[11px] uppercase tracking-wider text-slate-600">
              <th className="px-3 py-2">#</th><th className="px-3 py-2">Item</th>
              <th className="px-3 py-2">HSN</th><th className="px-3 py-2 text-right">Qty</th>
              <th className="px-3 py-2 text-right">Rate</th><th className="px-3 py-2 text-right">Taxable</th>
              <th className="px-3 py-2 text-right">GST %</th><th className="px-3 py-2 text-right">GST Amt</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {q.items.map((it, i) => (
              <tr key={i}>
                <td className="px-3 py-2.5 text-slate-500">{i + 1}</td>
                <td className="px-3 py-2.5 font-medium">{it.name}</td>
                <td className="px-3 py-2.5 font-mono text-xs">{it.hsn || "—"}</td>
                <td className="px-3 py-2.5 text-right">{it.qty} {it.unit}</td>
                <td className="px-3 py-2.5 text-right font-mono">{inr(it.rate)}</td>
                <td className="px-3 py-2.5 text-right font-mono">{inr(it.amount)}</td>
                <td className="px-3 py-2.5 text-right">{it.gst_rate}%</td>
                <td className="px-3 py-2.5 text-right font-mono">{inr(it.gst_amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="flex justify-end mt-5">
          <div className="w-72 text-sm space-y-1.5">
            {q.discount_amount > 0 && (
              <>
                <div className="flex justify-between"><span className="text-slate-600">Subtotal</span><span className="font-mono">{inr(q.subtotal)}</span></div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Less: Discount {q.discount_type === "percent" ? `(${q.discount_value}%)` : ""}</span>
                  <span className="font-mono text-red-700 whitespace-nowrap" data-testid="quotation-discount-print">− {inr(q.discount_amount)}</span>
                </div>
              </>
            )}
            <div className="flex justify-between"><span className="text-slate-600">Taxable Value</span><span className="font-mono">{inr(q.taxable ?? q.subtotal)}</span></div>
            {q.tax_type === "igst" ? (
              <div className="flex justify-between"><span className="text-slate-600">IGST</span><span className="font-mono">{inr(q.gst_total)}</span></div>
            ) : (
              <>
                <div className="flex justify-between"><span className="text-slate-600">CGST</span><span className="font-mono">{inr(cgst)}</span></div>
                <div className="flex justify-between"><span className="text-slate-600">SGST</span><span className="font-mono">{inr(cgst)}</span></div>
              </>
            )}
            <div className="flex justify-between border-t-2 border-emerald-600 pt-2 mt-2">
              <span className="font-bold">Quote Total</span>
              <span className="font-mono font-extrabold text-lg text-emerald-700" data-testid="quotation-total">{inr(q.grand_total)}</span>
            </div>
          </div>
        </div>

        {q.bank_details && (
          <div className="mt-5 max-w-md rounded-lg border border-slate-300 bg-slate-50 px-4 py-3" data-testid="quotation-bank-details">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Bank Details</p>
            <p className="text-sm font-semibold text-slate-900">{q.bank_details.name}{q.bank_details.bank ? ` · ${q.bank_details.bank}` : ""}</p>
            <p className="text-xs font-mono text-slate-700">
              {q.bank_details.account_number && <>A/c No: <b>{q.bank_details.account_number}</b></>}
              {q.bank_details.ifsc && <> · IFSC: <b>{q.bank_details.ifsc}</b></>}
            </p>
          </div>
        )}

        <div className="mt-10 pt-5 border-t border-slate-300 flex items-end justify-between">
          <div className="text-xs text-slate-500 max-w-[380px]">
            <p className="font-semibold text-slate-700 mb-1">Terms</p>
            <p>This is a proforma invoice / quotation valid until {fdate(q.valid_until)}. Prices are subject to change after the validity date. Taxes as applicable at the time of final invoice.</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-slate-500 mb-8">For {company.company_name}</p>
            <p className="text-xs font-semibold border-t border-slate-400 pt-1.5 px-6">Authorised Signatory</p>
          </div>
        </div>
      </div>
    </div>
  );
}
